import type { EventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import type { MeetingPublic } from '@repo/types/meetings';

import { AgendaDaysToShow } from '@repo/lib/constants/calendar-constants';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { VIEW_KEY } from '@repo/types/enums/viewKey';
import { addDays, format, isToday } from 'date-fns';
import { CalendarFold, LoaderCircle } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { EventItem } from '@/components/features/calendar/EventItem';
import { Button } from '@/components/ui/button';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { getCalendarEventUniqueId, type CalendarEvent, getAgendaEventsForDay, isHolidayCalendarEvent } from '@/utils/calendar';

import { useCalendarEvents } from './useCalendarEvents';

interface AgendaViewProps {
  rangeDates: EventCalendarRangeDates;
  currentDate: Date;
  onEventSelect: (event: MeetingPublic) => void;
}

export function AgendaView(agendaViewProps: AgendaViewProps) {
  const { rangeDates, currentDate, onEventSelect } = agendaViewProps;
  const days = useMemo(() => {
    return Array.from({ length: AgendaDaysToShow }, (_, i) => addDays(new Date(currentDate), i));
  }, [currentDate]);

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isHolidayCalendarEvent(event)) return;
    onEventSelect(event);
  };
  const { events, isFetching, isError, error, refetch } = useCalendarEvents(rangeDates);
  const hasEvents = days.some((day) => getAgendaEventsForDay(events, day).length > 0);

  useEffect(() => {
    if (isError) {
      toasterMessageHandler(baseErrorMessages, error, {
        action: (
          <Button
            size={'sm'}
            className={'ml-auto'}
            variant={'tertiary'}
            onClick={() => {
              void refetch();
            }}
          >
            Ponów
          </Button>
        ),
      });
    }
  }, [error, isError, refetch]);

  return (
    <div className="border-border-primary/70 border-t px-4">
      {isFetching && (
        <div className="flex h-full items-center justify-center py-14">
          <LoaderCircle className="text-foreground size-6 animate-spin" />
        </div>
      )}
      {!hasEvents && !isFetching ? (
        <EmptyState>
          <EmptyStateCircles className={'z-0'} />
          <FeaturedIcon size={'lg'} className={'z-10'}>
            <CalendarFold />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>Brak nadchodzących spotkań</EmptyStateTitle>
            <EmptyStateDescription>Brak spotkań w majbliższym miesiącu.</EmptyStateDescription>
          </EmptyStateContent>
        </EmptyState>
      ) : (
        days.map((day) => {
          const dayEvents = getAgendaEventsForDay(events, day);

          if (dayEvents.length === 0) return null;

          return (
            <div key={day.toString()} className="border-border-primary/70 relative my-12 border-t">
              <span
                className="bg-background-dark md:bg-background text-accent-foreground absolute -top-3 left-0 flex h-6 items-center pe-4 text-[10px] uppercase data-today:font-medium sm:pe-4 sm:text-xs"
                data-today={isToday(day) || undefined}
              >
                {format(day, 'd MMM, EEEE')}
              </span>
              <div className="mt-6 space-y-2">
                {dayEvents.map((event) => (
                  <EventItem key={getCalendarEventUniqueId(event)} event={event} view={VIEW_KEY.AGENDA} onClick={(e) => handleEventClick(event, e)} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
